/*
 * © 2026 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { createUniqueMeasurementID } from "@/presentation/MeasurementCharacteristic";

/**
 * Keeps the measurement detail and summary tables in the same row
 * order without forcing them to share a sort function.
 *
 * The two tables have mostly disjoint columns, so they each keep their own sort
 * column, direction and sort function (see ./measurements and ./statistics).
 * Whichever table the user last sorted produces the canonical order; that order
 * is reduced to a key -> index map here and handed to *both* tables, so the one
 * that did not produce it still renders in the same order.
 *
 * The key is `createUniqueMeasurementID` (`featureName/name`), which both
 * datasets can produce, so neither table needs to know anything about the
 * other's columns.
 */

/** Maps a unique measurement id to its position in the shared row order. */
export type MeasurementRowOrder = ReadonlyMap<string, number>;

export type MeasurementSortDirection = "asc" | "desc";

/** Anything either table can render, which is all we need to key a row. */
export type KeyableRow = { featureName?: string; name: string };

/** Reduces an already sorted list to the order the other table should follow. */
export function toMeasurementRowOrder(
  sortedRows: readonly KeyableRow[]
): MeasurementRowOrder {
  const order = new Map<string, number>();

  sortedRows.forEach((row) => {
    const key = createUniqueMeasurementID(row);
    if (!order.has(key)) order.set(key, order.size);
  });

  return order;
}

/**
 * Orders a table's rows by the shared canonical order.
 *
 * Rows missing from the order are appended, keeping their original
 * relative position. Both tables can have rows the other does not: the summary
 * includes characteristics from other runs of the job, and the detail table can
 * include measurements dropped from the statistics for having a non-finite
 * actual.
 */
export function applyMeasurementRowOrder<T extends KeyableRow>(
  rows: readonly T[],
  order: MeasurementRowOrder
): T[] {
  const unknownPosition = order.size;

  return rows
    .map((row, index) => ({
      row,
      index,
      position: order.get(createUniqueMeasurementID(row)) ?? unknownPosition,
    }))
    .sort((a, b) => a.position - b.position || a.index - b.index)
    .map((entry) => entry.row);
}
